import { useCallback, useEffect, useMemo, useState } from "react";
import { Check, Eye, Hash, RotateCcw, X } from "lucide-react";
import {
  BAND_LABEL, ORIGIN_LABEL, STATUS_LABEL, courseLibraryApi,
  type BulkAction, type BulkPreview, type BulkResult, type LibFilter, type LibItem, type LibKind, type LibList, type LibStatus,
} from "../../lib/courseLibraryApi";
import { Badge } from "../ui/Badge";
import { Button } from "../ui/Button";
import { Modal } from "../ui/Modal";
import { toast } from "../ui/Toast";
import { mapApiError } from "../ui/StatusMessage";
import { AdminDataTable, type Column, type RowAction } from "./dataTable/AdminDataTable";
import { BulkBar } from "./dataTable/BulkBar";
import { FilterBar } from "./dataTable/FilterBar";
import { useListState } from "./dataTable/useListState";
import { AiStagingToggle } from "./AiStagingToggle";
import { LibraryItemDrawer } from "./LibraryItemDrawer";

type Props = { token: string; courseId: string; kind: LibKind };

const NOUN: Record<LibKind, string> = { quiz: "quiz questions", note: "note topics", past_exam: "past exam questions" };
const ACTION_TEXT: Record<BulkAction, string> = {
  approve: "Approve", reject: "Reject", send_back: "Send back to draft", regenerate: "Send for regeneration", set_difficulty: "Change difficulty", move_chapter: "Move to chapter",
};

/** Every generated item of one course, in every status, with combinable filters kept in the URL. */
export function CourseItemLibrary({ token, courseId, kind }: Props) {
  const list = useListState(kind === "quiz" ? "lq" : kind === "note" ? "ln" : "le", ["status", "band", "difficulty", "figure", "source", "mode", "chapter", "topic", "minc", "maxc"], { key: "chapter", dir: "asc" });
  const f = list.filters;

  const [data, setData] = useState<LibList | null>(null);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [openId, setOpenId] = useState<string | null>(null);
  const [bulk, setBulk] = useState<{ action: BulkAction; ids?: string[] } | null>(null);

  const filter: LibFilter = useMemo(
    () => ({
      status: f.status || undefined, band: f.band || undefined, difficulty: f.difficulty || undefined,
      figure: (f.figure as "yes" | "no") || undefined, source: (f.source as "yes" | "no") || undefined, mode: (f.mode as LibFilter["mode"]) || undefined,
      chapter: f.chapter || undefined, topic: f.topic || undefined, q: list.q || undefined,
      min_conf: f.minc ? Number(f.minc) / 100 : undefined, max_conf: f.maxc ? Number(f.maxc) / 100 : undefined,
    }),
    [f.status, f.band, f.difficulty, f.figure, f.source, f.mode, f.chapter, f.topic, f.minc, f.maxc, list.q],
  );

  const load = useCallback(async () => {
    try {
      setData(await courseLibraryApi.list(token, courseId, kind, filter));
    } catch (e) {
      toast.error(mapApiError(e).message, { action: { label: "Try again", onClick: () => void load() } });
    } finally {
      setLoading(false);
    }
  }, [token, courseId, kind, filter]);

  useEffect(() => {
    const t = window.setTimeout(() => void load(), 250);
    return () => window.clearTimeout(t);
  }, [load]);

  useEffect(() => setSelected(new Set()), [filter]);

  const facets = data?.facets;
  const ids = data?.items.map((i) => i.id) ?? [];
  const openItem = data?.items.find((i) => i.id === openId) ?? null;

  const chips = (key: string, label: string, counts: Record<string, number> | undefined, labels: Record<string, string>) => ({
    key,
    label,
    options: Object.entries(labels).map(([value, text]) => ({ value, label: `${text} (${counts?.[value] ?? 0})` })),
  });

  const columns: Column<LibItem>[] = [
    { key: "status", header: "Status", className: "w-32 shrink-0", cell: (i) => <Badge variant={i.status === "published" ? "success" : i.status === "rejected" ? "destructive" : i.status === "needs_review" ? "warning" : "secondary"}>{STATUS_LABEL[i.status]}</Badge> },
    {
      key: "confidence", header: "Confidence", className: "w-32 shrink-0", sortValue: (i) => i.confidence ?? -1,
      cell: (i) => <Badge variant={i.band === "high" ? "success" : i.band === "low" ? "destructive" : "secondary"}>{BAND_LABEL[i.band]}{i.confidence != null ? ` ${Math.round(i.confidence * 100)}%` : ""}</Badge>,
    },
    { key: "mode", header: "Origin", className: "w-28 shrink-0", desktopOnly: true, cell: (i) => <Badge variant="secondary">{ORIGIN_LABEL[i.mode] ?? "Unknown"}</Badge> },
    { key: "chapter", header: "Chapter", className: "w-36 shrink-0", desktopOnly: true, cell: (i) => <span className="truncate text-muted-foreground">{i.chapter_order != null ? `Ch ${i.chapter_order}` : "No chapter"}{i.difficulty ? ` · ${i.difficulty}` : ""}</span> },
  ];

  const actions: RowAction<LibItem>[] = [
    { key: "open", label: "Open and review", icon: <Eye size={14} />, group: 0, quick: true, onSelect: (i) => setOpenId(i.id) },
    { key: "approve", label: "Approve", icon: <Check size={14} />, group: 1, onSelect: (i) => setBulk({ action: "approve", ids: [i.id] }), hidden: (i) => i.status === "published" },
    { key: "back", label: "Send back to draft", icon: <RotateCcw size={14} />, group: 1, onSelect: (i) => setBulk({ action: "send_back", ids: [i.id] }), hidden: (i) => i.status !== "published" },
    { key: "reject", label: "Reject", icon: <X size={14} />, group: 2, danger: true, onSelect: (i) => setBulk({ action: "reject", ids: [i.id] }), hidden: (i) => i.status === "rejected" || i.status === "published" },
    { key: "id", label: "Copy ID", icon: <Hash size={14} />, group: 3, onSelect: (i) => void navigator.clipboard?.writeText(i.external_id) },
  ];

  const selectedIds = [...selected];

  return (
    <div className="space-y-3">
      <AiStagingToggle token={token} />
      <FilterBar
        list={list}
        searchLabel={`Search ${NOUN[kind]}`}
        filters={[
          chips("status", "Status", facets?.status, { draft: "Draft", needs_review: "Needs review", published: "Approved", rejected: "Rejected" }),
          chips("band", "Confidence", facets?.band, { high: "High", medium: "Medium", low: "Low", unscored: "Not scored" }),
          ...(kind !== "note" ? [chips("difficulty", "Difficulty", facets?.difficulty, Object.fromEntries(Object.keys(facets?.difficulty ?? {}).map((d) => [d, d === "none" ? "Not set" : d])))] : []),
          chips("figure", "Figure", facets?.figure, { yes: "Has figure", no: "No figure" }),
          chips("source", "Source", facets?.source, { yes: "Has source", no: "Missing source" }),
          chips("mode", "Origin", facets?.mode, { pipeline: "Pipeline", ai_api: "AI API", unknown: "Unknown" }),
        ]}
      />

      <div className="grid gap-3 rounded-tile border border-border bg-card p-3 sm:grid-cols-2 lg:grid-cols-3">
        <label className="block text-xs font-semibold text-muted-foreground">Topic
          <select value={f.topic ?? ""} onChange={(e) => list.setFilter("topic", e.target.value)} className="field mt-1 w-full rounded-control px-2 py-2 text-base sm:text-sm">
            <option value="">All topics</option>
            {Object.entries(facets?.topics ?? {}).map(([t, n]) => <option key={t} value={t}>{t} ({n})</option>)}
          </select>
        </label>
        <label className="block text-xs font-semibold text-muted-foreground">Confidence from {f.minc || 0}%
          <input type="range" min={0} max={100} step={5} value={f.minc || 0} onChange={(e) => list.setFilter("minc", e.target.value === "0" ? "" : e.target.value)} className="mt-2 w-full accent-primary" />
        </label>
        <label className="block text-xs font-semibold text-muted-foreground">Confidence up to {f.maxc || 100}%
          <input type="range" min={0} max={100} step={5} value={f.maxc || 100} onChange={(e) => list.setFilter("maxc", e.target.value === "100" ? "" : e.target.value)} className="mt-2 w-full accent-primary" />
        </label>
      </div>

      {facets && facets.chapters.length > 0 && (
        <section aria-label="Chapters" className="space-y-2">
          <div className="flex items-center justify-between">
            <h3 className="text-sm font-semibold text-foreground">Chapters</h3>
            {f.chapter && (
              <button type="button" onClick={() => { list.setFilter("chapter", ""); list.setFilter("topic", ""); }} className="min-h-9 px-2 text-sm font-semibold text-primary hover:underline">Show all</button>
            )}
          </div>
          <ul className="flex gap-2 overflow-x-auto pb-1 [scrollbar-width:thin]">
            {facets.chapters.map((c) => {
              const active = f.chapter === c.key;
              return (
                <li key={c.key} className="shrink-0">
                  <button
                    type="button"
                    aria-pressed={active}
                    onClick={() => { list.setFilter("chapter", active ? "" : c.key); list.setFilter("topic", ""); }}
                    className={`w-52 space-y-1 rounded-tile border p-3 text-left transition-colors focus-visible:ring-2 ring-ring ${active ? "border-primary bg-soft" : "border-border bg-card hover:bg-soft"}`}
                  >
                    <span className="flex items-center justify-between gap-2">
                      <span className="truncate text-sm font-semibold text-foreground">{c.order != null ? `${c.order}. ` : ""}{c.title}</span>
                      <span className="shrink-0 rounded-full bg-primary px-2 text-xs font-bold text-on-accent">{c.count}</span>
                    </span>
                    <span className="block truncate text-xs text-muted-foreground">
                      {Object.entries(c.byDifficulty).map(([d, n]) => `${d === "none" ? "Not set" : d}: ${n}`).join(" · ")}
                    </span>
                  </button>
                </li>
              );
            })}
          </ul>
        </section>
      )}

      <div className="flex flex-wrap items-center justify-between gap-2 text-sm">
        <p className="text-muted-foreground">
          <span className="font-semibold text-foreground">{data?.total ?? 0}</span> of {data?.all ?? 0} {NOUN[kind]}
          {data?.capped ? " (showing the first 600)" : ""}
        </p>
        {data && data.total > 0 && (
          <div className="flex flex-wrap gap-2">
            <Button size="sm" onClick={() => setBulk({ action: "approve" })} icon={<Check size={14} />}>Approve all {data.total} shown</Button>
            <Button size="sm" variant="outline" onClick={() => setBulk({ action: "regenerate" })}>Regenerate all shown</Button>
          </div>
        )}
      </div>

      <AdminDataTable<LibItem>
        rows={data?.items ?? []}
        rowId={(i) => i.id}
        noun={NOUN[kind]}
        loading={loading}
        columns={columns}
        actions={actions}
        list={list}
        selected={selected}
        onSelectedChange={setSelected}
        onOpen={(i) => setOpenId(i.id)}
        pageSize={25}
        primary={(i) => (i.status === "draft" || i.status === "needs_review" ? { label: "Review", onClick: () => setOpenId(i.id) } : null)}
        titleCell={(i) => (
          <span className="min-w-0">
            <span className="block truncate text-sm font-semibold text-foreground">{i.text || i.topic || i.external_id}</span>
            <span className="block truncate text-xs text-muted-foreground">
              {[i.kind === "note" ? i.topic : `Q${i.number ?? ""}`, i.chapter_title, i.has_figure ? "figure" : "", i.has_source ? "" : "no source"].filter(Boolean).join(" · ")}
            </span>
          </span>
        )}
        empty={{ title: `No ${NOUN[kind]} yet`, message: "Generated items appear here after the content pipeline runs for this course." }}
      />

      <BulkBar
        count={selected.size}
        noun={NOUN[kind]}
        total={data?.matching_ids.length}
        onSelectAll={() => setSelected(new Set(data?.matching_ids ?? []))}
        onClear={() => setSelected(new Set())}
        actions={[
          { key: "approve", label: "Approve", icon: <Check size={14} />, onClick: () => setBulk({ action: "approve", ids: selectedIds }) },
          { key: "back", label: "Send back", onClick: () => setBulk({ action: "send_back", ids: selectedIds }) },
          { key: "regen", label: "Regenerate", onClick: () => setBulk({ action: "regenerate", ids: selectedIds }) },
          { key: "diff", label: "Difficulty", onClick: () => setBulk({ action: "set_difficulty", ids: selectedIds }) },
          { key: "move", label: "Move chapter", onClick: () => setBulk({ action: "move_chapter", ids: selectedIds }) },
          { key: "reject", label: "Reject", danger: true, onClick: () => setBulk({ action: "reject", ids: selectedIds }) },
        ]}
      />

      <LibraryItemDrawer
        open={Boolean(openId)}
        item={openItem}
        ids={ids}
        token={token}
        courseId={courseId}
        kind={kind}
        chapters={facets?.chapters ?? []}
        onNavigate={setOpenId}
        onClose={() => setOpenId(null)}
        onChanged={() => void load()}
      />

      {bulk && (
        <BulkDialog
          token={token}
          courseId={courseId}
          kind={kind}
          action={bulk.action}
          ids={bulk.ids}
          filter={filter}
          chapters={facets?.chapters ?? []}
          difficulties={Object.keys(facets?.difficulty ?? {}).filter((d) => d !== "none")}
          onClose={() => setBulk(null)}
          onDone={() => {
            setBulk(null);
            setSelected(new Set());
            void load();
          }}
        />
      )}
    </div>
  );
}

/** Confirmation with a real preview (a dry run): exactly how many items change, what is left out, and an Undo afterwards. */
function BulkDialog({
  token, courseId, kind, action, ids, filter, chapters, difficulties, onClose, onDone,
}: {
  token: string; courseId: string; kind: LibKind; action: BulkAction; ids?: string[]; filter: LibFilter;
  chapters: { key: string; order: number | null; title: string }[]; difficulties: string[]; onClose: () => void; onDone: () => void;
}) {
  const [preview, setPreview] = useState<BulkPreview | null>(null);
  const [includeLow, setIncludeLow] = useState(false);
  const [note, setNote] = useState("");
  const [difficulty, setDifficulty] = useState(difficulties[0] ?? "");
  const [chapterKey, setChapterKey] = useState("");
  const [busy, setBusy] = useState(false);

  const chapter = chapters.find((c) => c.key === chapterKey);
  const body = useMemo(
    () => ({
      kind, action, ids, filter: ids?.length ? undefined : filter, include_low: includeLow, note: note.trim() || undefined, difficulty: difficulty || undefined,
      chapter_order: chapter?.order ?? undefined, chapter_title: chapter?.title,
    }),
    [kind, action, ids, filter, includeLow, note, difficulty, chapter],
  );

  useEffect(() => {
    let cancelled = false;
    courseLibraryApi
      .bulk(token, courseId, { ...body, dry_run: true })
      .then((r) => !cancelled && setPreview(r as BulkPreview))
      .catch((e) => !cancelled && toast.error(mapApiError(e).message));
    return () => {
      cancelled = true;
    };
  }, [token, courseId, body.include_low, body.action]); // eslint-disable-line react-hooks/exhaustive-deps

  const needsNote = action === "reject";
  const ready = preview && preview.will_change > 0 && (!needsNote || note.trim()) && (action !== "set_difficulty" || difficulty) && (action !== "move_chapter" || chapter?.order != null);

  async function run() {
    setBusy(true);
    try {
      const r = (await courseLibraryApi.bulk(token, courseId, body)) as BulkResult;
      const left = r.left_out_low_confidence ? ` ${r.left_out_low_confidence} low-confidence left out.` : "";
      const blocked = r.blocked?.length ? ` ${r.blocked.reduce((n, b) => n + b.item_ids.length, 0)} need a decision in Content review.` : "";
      const batch = r.batch_id;
      toast.success(`${ACTION_TEXT[action]}: ${r.changed ?? 0} done.${left}${blocked}`, {
        duration: 10000,
        action: batch
          ? {
              label: "Undo",
              onClick: () => {
                courseLibraryApi.undo(token, courseId, batch).then((u) => { toast.info(u.note ?? `${u.restored} restored`); onDone(); }).catch((e) => toast.error(mapApiError(e).message));
              },
            }
          : undefined,
      });
      onDone();
    } catch (e) {
      toast.error(mapApiError(e).message, { action: { label: "Try again", onClick: () => void run() } });
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen onClose={onClose} title={ACTION_TEXT[action]} size="sm">
      <div className="space-y-3 text-sm">
        {!preview ? <p className="text-muted-foreground">Checking…</p> : (
          <p>
            <span className="font-semibold text-foreground">{preview.will_change}</span> {preview.will_change === 1 ? "item" : "items"} will change
            {preview.unchanged > 0 && <span className="text-muted-foreground"> · {preview.unchanged} already there</span>}.
          </p>
        )}
        {action === "approve" && preview && preview.left_out_low_confidence > 0 && (
          <label className="flex items-start gap-2 rounded-tile bg-soft p-2">
            <input type="checkbox" checked={includeLow} onChange={(e) => setIncludeLow(e.target.checked)} className="mt-1 h-4 w-4" />
            <span>{preview.left_out_low_confidence} low-confidence {preview.left_out_low_confidence === 1 ? "item is" : "items are"} left out. Include {preview.left_out_low_confidence === 1 ? "it" : "them"} too.</span>
          </label>
        )}
        {(action === "reject" || action === "regenerate") && (
          <label className="block font-semibold">{action === "reject" ? "What is wrong? The next run reads this." : "What should change? (optional)"}
            <textarea className="field mt-1 min-h-20 w-full rounded-control px-3 py-2 text-base sm:text-sm" value={note} onChange={(e) => setNote(e.target.value)} />
          </label>
        )}
        {action === "set_difficulty" && (
          <label className="block font-semibold">New difficulty
            <input list="lib-difficulties" className="field mt-1 w-full rounded-control px-3 py-2 text-base sm:text-sm" value={difficulty} onChange={(e) => setDifficulty(e.target.value)} />
            <datalist id="lib-difficulties">{difficulties.map((d) => <option key={d} value={d} />)}</datalist>
          </label>
        )}
        {action === "move_chapter" && (
          <label className="block font-semibold">Chapter
            <select className="field mt-1 w-full rounded-control px-3 py-2 text-base sm:text-sm" value={chapterKey} onChange={(e) => setChapterKey(e.target.value)}>
              <option value="">Choose a chapter</option>
              {chapters.filter((c) => c.order != null).map((c) => <option key={c.key} value={c.key}>{c.order}. {c.title}</option>)}
            </select>
          </label>
        )}
        <p className="text-xs text-muted-foreground">This is saved in the admin log. You can undo it for a few minutes.</p>
        <div className="flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button loading={busy} loadingText="Working…" disabled={!ready} onClick={() => void run()} variant={action === "reject" ? "destructive" : "primary"}>
            {ACTION_TEXT[action]} {preview ? preview.will_change : ""}
          </Button>
        </div>
      </div>
    </Modal>
  );
}

export type { LibStatus };
